import React from 'react';
import { Check, Download, Calendar, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

interface SubscriptionSuccessModalProps {
  isOpen: boolean;
  onClose: () => void;
  planName: string;
  amount?: string;
  nextBillingDate?: string;
  isRenewal?: boolean;
  licenseKey?: string | null; // Novo campo opcional
}

const SubscriptionSuccessModal: React.FC<SubscriptionSuccessModalProps> = ({
  isOpen,
  onClose,
  planName,
  nextBillingDate,
  isRenewal = false,
  licenseKey // Recebe a chave
}) => {
  if (!isOpen) return null;

  // Formatar data se fornecida, senão usar data de hoje + 1 mês (estimativa)
  const expirationDate = nextBillingDate 
    ? new Date(nextBillingDate).toLocaleDateString('pt-BR') 
    : new Date(new Date().setMonth(new Date().getMonth() + 1)).toLocaleDateString('pt-BR');

  const isMelhoradorCloud = planName.toLowerCase().includes('melhorador cloud');
  const showLicenseKey = licenseKey && !isMelhoradorCloud;

  const copyToClipboard = () => {
    if (licenseKey) {
      navigator.clipboard.writeText(licenseKey);
      // O ideal seria um toast aqui, mas vamos manter simples por enquanto
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 animate-in fade-in duration-200">
      <Card className="w-full max-w-md bg-zinc-950 border-green-500/30 shadow-2xl shadow-green-900/20 animate-in zoom-in-95 duration-300">
        <CardHeader className="text-center pb-2">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-500/10 border-2 border-green-500/20">
            <Check className="h-8 w-8 text-green-500 animate-in zoom-in duration-500 delay-150" strokeWidth={3} />
          </div>
          <CardTitle className="text-2xl font-bold text-white">
            {isRenewal ? 'Licença Prolongada!' : 'Assinatura Confirmada!'}
          </CardTitle>
          <p className="text-zinc-400 mt-2">
            {isRenewal ? (
              <>Sua assinatura do <span className="text-green-400 font-medium">{planName}</span> foi renovada.</>
            ) : (
              <>Bem-vindo(a) ao <span className="text-green-400 font-medium">{planName}</span></>
            )}
          </p>
        </CardHeader>
        
        <CardContent className="space-y-6 pt-4">
          
          {/* Se tiver KeyAuth License Key, mostra ela com destaque (exceto para Melhorador Cloud) */}
          {showLicenseKey && (
             <div className="rounded-lg bg-green-500/10 border border-green-500/30 p-4 space-y-2">
                <p className="text-xs text-green-400 font-semibold uppercase tracking-wider text-center">Sua Chave de Ativação</p>
                <div className="flex items-center gap-2">
                    <code className="flex-1 bg-black/50 p-3 rounded border border-green-500/20 text-green-300 font-mono text-center text-lg select-all">
                        {licenseKey}
                    </code>
                    <Button 
                        size="icon" 
                        variant="outline" 
                        className="border-green-500/30 hover:bg-green-500/20 text-green-400"
                        onClick={copyToClipboard}
                    >
                        <Download className="h-4 w-4" /> {/* Usando ícone existente, ideal seria Copy */}
                    </Button>
                </div>
                <p className="text-[10px] text-zinc-500 text-center">
                    Copie e cole esta chave no software para ativar.
                </p>
             </div>
          )}

          {/* Info Box */}
          <div className="rounded-lg bg-zinc-900/50 border border-zinc-800 p-4 space-y-3">
            <div className="flex items-center justify-between text-sm">
              <span className="text-zinc-400 flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                {isRenewal ? 'Nova validade:' : 'Próxima cobrança:'}
              </span>
              <span className="text-white font-medium">{expirationDate}</span>
            </div>
            
            <div className="h-px bg-zinc-800/50" />
            
            <p className="text-xs text-zinc-500 leading-relaxed text-center pt-1">
              {isRenewal 
                ? 'Seus tokens foram recarregados e o prazo estendido.' 
                : 'Sua licença já está ativa e vinculada à sua conta.'}
            </p>
          </div>

          {/* Instructions */}
          <div className="space-y-3">
            <h4 className="text-sm font-medium text-zinc-300">
              {isRenewal ? 'O que acontece agora?' : 'Próximos passos:'}
            </h4>
            <ul className="space-y-2 text-sm text-zinc-400">
              {isRenewal ? (
                <>
                  <li className="flex items-start gap-2">
                    <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-zinc-800 text-[10px] font-bold text-zinc-300">1</span>
                    <span>Seu tempo de uso foi somado ao restante anterior.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-zinc-800 text-[10px] font-bold text-zinc-300">2</span>
                    <span>Novos tokens foram adicionados à sua conta.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-zinc-800 text-[10px] font-bold text-zinc-300">3</span>
                    <span>Continue usando normalmente, sem interrupções!</span>
                  </li>
                </>
              ) : isMelhoradorCloud ? (
                // PASSOS PARA MELHORADOR CLOUD (VIA LAUNCHER)
                <>
                  <li className="flex items-start gap-2">
                    <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-zinc-800 text-[10px] font-bold text-zinc-300">1</span>
                    <span>Baixe ou acesse o <strong>Launcher</strong>.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-zinc-800 text-[10px] font-bold text-zinc-300">2</span>
                    <span>Acesse a aba <strong>Library</strong>.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-zinc-800 text-[10px] font-bold text-zinc-300">3</span>
                    <span>Baixe o <strong>Melhorador Cloud</strong>.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-zinc-800 text-[10px] font-bold text-zinc-300">4</span>
                    <span>Abra o aplicativo e comece a utilizar!</span>
                  </li>
                </>
              ) : licenseKey ? (
                // PASSOS ESPECÍFICOS PARA O MELHORADOR (COM KEYAUTH)
                <>
                  <li className="flex items-start gap-2">
                    <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-zinc-800 text-[10px] font-bold text-zinc-300">1</span>
                    <span>Copie a chave acima (botão ao lado do código).</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-zinc-800 text-[10px] font-bold text-zinc-300">2</span>
                    <span>Baixe e instale <strong>O Melhorador</strong> no seu PC.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-zinc-800 text-[10px] font-bold text-zinc-300">3</span>
                    <span>Abra o programa e insira a chave no campo <strong>Key</strong> na primeira tela.</span>
                  </li>
                </>
              ) : (
                // PASSOS PADRÃO (OVERPIXEL LAUNCHER)
                <>
                  <li className="flex items-start gap-2">
                    <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-zinc-800 text-[10px] font-bold text-zinc-300">1</span>
                    <span>Abra o <strong>OverPixel Launcher</strong> no seu computador.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-zinc-800 text-[10px] font-bold text-zinc-300">2</span>
                    <span>Faça login (ou reinicie se já estiver aberto).</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-zinc-800 text-[10px] font-bold text-zinc-300">3</span>
                    <span>Seu novo plano será reconhecido automaticamente!</span>
                  </li>
                </>
              )}
            </ul>
          </div>

          {/* Action Button */}
          <Button 
            onClick={onClose} 
            className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-6 text-lg shadow-lg shadow-green-900/20 transition-all hover:scale-[1.02]"
          >
            {isRenewal ? 'Maravilha, continuar!' : 'Entendi, começar a usar'}
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default SubscriptionSuccessModal;
